<template>
  <a-modal
    :visible="visible"
    :width="720"
    :title="modalTitle"
    wrap-class-name="calculation-sheet-modal"
    @cancel="handleClose"
  >
    <div v-if="loading" class="calculation-sheet__loading">
      <a-spin />
    </div>

    <div
      v-else-if="fetchError"
      class="calculation-sheet__feedback calculation-sheet__feedback--error"
    >
      <a-icon type="exclamation-circle" />
      <span class="calculation-sheet__feedback-text">{{ fetchError }}</span>
      <a-button size="small" @click="load">Tentar novamente</a-button>
    </div>

    <div v-else-if="sheet" class="calculation-sheet">
      <div
        v-for="(warning, index) in sheet.warnings"
        :key="warning.code + '-' + index"
        class="calculation-sheet__banner"
        :class="'calculation-sheet__banner--' + warning.severity"
      >
        <a-icon :type="warningIcon(warning.severity)" />
        <span>{{ warning.message }}</span>
      </div>

      <div
        v-if="sheet.hasCarrierScopeNote"
        class="calculation-sheet__banner calculation-sheet__banner--info"
      >
        <a-icon type="info-circle" />
        <span>{{ CARRIER_SCOPE_NOTE }}</span>
      </div>

      <div
        v-if="!sheet.isComplete"
        class="calculation-sheet__banner calculation-sheet__banner--degrading"
      >
        <a-icon type="warning" />
        <span>{{ INCOMPLETE_SHEET_NOTE }}</span>
      </div>

      <section class="calculation-sheet__section">
        <h3 class="calculation-sheet__section-title">Carga</h3>
        <div class="calculation-sheet__grid">
          <div
            v-for="field in shipmentFields"
            :key="field.label"
            class="calculation-sheet__field"
            :class="fieldClass(field)"
          >
            <span class="calculation-sheet__label">{{ field.label }}</span>
            <span class="calculation-sheet__value">{{ field.value }}</span>
          </div>
        </div>
      </section>

      <section class="calculation-sheet__section">
        <h3 class="calculation-sheet__section-title">Cálculo do frete</h3>

        <div
          v-if="!sheet.freight && sheet.manualExpense"
          class="calculation-sheet__total calculation-sheet__total--standalone"
        >
          <span class="calculation-sheet__total-label">Frete avulso</span>
          <span class="calculation-sheet__total-value">
            {{ sheet.manualExpense.value }}
          </span>
        </div>

        <p v-else-if="!sheet.freight" class="calculation-sheet__empty">
          Não há dados de frete para exibir nesta carga. O motivo está no aviso
          acima.
        </p>

        <template v-else>
          <div class="calculation-sheet__grid">
            <div
              v-for="field in freightFields"
              :key="field.label"
              class="calculation-sheet__field"
              :class="fieldClass(field)"
            >
              <span class="calculation-sheet__label">
                {{ field.label }}
                <a-tooltip v-if="field.hint" :title="field.hint">
                  <a-icon type="info-circle" class="calculation-sheet__hint" />
                </a-tooltip>
              </span>
              <span class="calculation-sheet__value">
                {{ field.value }}
                <span v-if="field.suffix" class="calculation-sheet__suffix">
                  {{ field.suffix }}
                </span>
              </span>
            </div>
          </div>

          <div class="calculation-sheet__total">
            <span class="calculation-sheet__total-label">
              Valor total do frete
            </span>
            <span class="calculation-sheet__total-value">
              {{ sheet.freight.totalValue }}
            </span>
          </div>
        </template>
      </section>

      <section class="calculation-sheet__section">
        <h3 class="calculation-sheet__section-title">Pedágio</h3>
        <div class="calculation-sheet__grid">
          <div
            v-for="field in tollFields"
            :key="field.label"
            class="calculation-sheet__field"
          >
            <span class="calculation-sheet__label">{{ field.label }}</span>
            <span class="calculation-sheet__value">{{ field.value }}</span>
          </div>
        </div>
      </section>

      <div
        v-if="pdfLink"
        class="calculation-sheet__banner calculation-sheet__banner--success"
      >
        <a-icon type="check-circle" />
        <span>
          PDF gerado.
          <a
            :href="pdfLink"
            :download="pdfFileName"
            target="_blank"
            rel="noopener"
          >
            Baixar {{ pdfFileName }}
          </a>
        </span>
      </div>

      <div
        v-else-if="printMessage"
        class="calculation-sheet__banner calculation-sheet__banner--success"
      >
        <a-icon type="check-circle" />
        <span v-html="printMessage"></span>
      </div>

      <div
        v-if="printError"
        class="calculation-sheet__banner calculation-sheet__banner--degrading"
      >
        <a-icon type="warning" />
        <span>{{ printError }}</span>
      </div>
    </div>

    <template slot="footer">
      <a-button :disabled="printing" @click="handleClose">Fechar</a-button>
      <a-button
        type="primary"
        :loading="printing"
        :disabled="!sheet"
        @click="handlePrint"
      >
        Gerar PDF
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import CalculationSheetApi from "../services/CalculationSheetApi";
import { mapCalculationSheet } from "../utils/calculationSheetMapper";
import {
  describeFetchError,
  describePrintError,
  MISSING_SHIPMENT_MESSAGE,
} from "../utils/calculationSheetErrors";
import {
  CARRIER_SCOPE_NOTE,
  INCOMPLETE_SHEET_NOTE,
  WARNING_SEVERITY,
} from "../constants/calculationWarnings";
import { extractPdfFileName, extractPdfHref } from "../utils/pdfDownload";

const PRINT_SUCCESS_FALLBACK = "PDF gerado com sucesso.";

export default {
  name: "CalculationSheetModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    cargaId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      CARRIER_SCOPE_NOTE,
      INCOMPLETE_SHEET_NOTE,
      sheet: null,
      loading: false,
      fetchError: null,
      printing: false,
      printError: null,
      printMessage: null,
      pdfLink: null,
      pdfFileName: null,
      requestSeq: 0,
    };
  },
  computed: {
    modalTitle() {
      if (this.sheet && this.sheet.shipment) {
        return "Minuta de Cálculo — Carga " + this.sheet.shipment.number;
      }
      return "Minuta de Cálculo";
    },
    shipmentFields() {
      const shipment = this.sheet.shipment;

      return [
        { label: "Nº Carga", value: shipment.number },
        { label: "Manifesto", value: shipment.manifestNumber },
        { label: "Expedição", value: shipment.dispatchDate },
        { label: "Transportadora", value: shipment.carrier.name, span: 2 },
        { label: "CNPJ", value: shipment.carrier.document },
      ];
    },
    freightFields() {
      const freight = this.sheet.freight;
      const calculationType = freight.calculationType;

      return [
        { label: "Tabela de frete", value: freight.table.label, span: 2 },
        {
          label: "Tipo de cálculo",
          value: calculationType.label,
          suffix: calculationType.code ? "(" + calculationType.code + ")" : null,
        },
        { label: "Rota", value: freight.route.label, span: 3, wrap: true },
        { label: "Data do cálculo", value: freight.calculationDate },
        {
          label: "Tarifa",
          value: freight.tariff.value,
          hint: freight.tariff.hint,
        },
      ];
    },
    tollFields() {
      const toll = this.sheet.toll;

      return [
        { label: "Vale-pedágio", value: toll.value },
        { label: "Nº do vale", value: toll.voucherNumber },
      ];
    },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.load();
      } else {
        this.reset();
      }
    },
    cargaId() {
      if (this.visible) this.load();
    },
  },
  methods: {
    reset() {
      this.sheet = null;
      this.loading = false;
      this.fetchError = null;
      this.printing = false;
      this.printError = null;
      this.clearPrintResult();
    },
    clearPrintResult() {
      this.pdfLink = null;
      this.pdfFileName = null;
      this.printMessage = null;
    },
    load() {
      if (!this.cargaId) {
        this.fetchError = MISSING_SHIPMENT_MESSAGE;
        return;
      }

      this.requestSeq += 1;
      const seq = this.requestSeq;

      this.loading = true;
      this.fetchError = null;
      this.printError = null;
      this.clearPrintResult();

      CalculationSheetApi.fetchSheet(this.cargaId)
        .then((response) => {
          if (seq !== this.requestSeq) return;
          this.sheet = mapCalculationSheet(response.data);
          this.loading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.sheet = null;
          this.fetchError = describeFetchError(error);
          this.loading = false;
        });
    },
    handlePrint() {
      this.printing = true;
      this.printError = null;
      this.clearPrintResult();

      CalculationSheetApi.printSheet(this.cargaId)
        .then((response) => {
          const body = response.data || {};
          const message = body.message || PRINT_SUCCESS_FALLBACK;
          const href = extractPdfHref(message);
          const link = CalculationSheetApi.resolvePdfUrl(href);

          if (link) {
            this.pdfLink = link;
            this.pdfFileName = extractPdfFileName(href);
          } else {
            this.printMessage = message;
          }

          this.printing = false;
        })
        .catch((error) => {
          this.printError = describePrintError(error);
          this.printing = false;
        });
    },
    handleClose() {
      this.$emit("close");
    },
    fieldClass(field) {
      return [
        "calculation-sheet__field--span-" + (field.span || 1),
        field.wrap ? "calculation-sheet__field--wrap" : "",
      ];
    },
    warningIcon(severity) {
      return severity === WARNING_SEVERITY.DEGRADING ? "warning" : "info-circle";
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.calculation-sheet {
  font-family: @font-primary;

  &__loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
  }

  &__feedback {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px 0;
    font-size: 14px;

    &--error {
      color: @color-danger-strong;
    }
  }

  &__feedback-text {
    flex: 1;
  }

  &__banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid transparent;
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 8px;

    &--info {
      background-color: @color-bg-info;
      border-color: @color-tag-border;
      color: @color-tag-text;
    }

    &--degrading {
      background-color: @color-tag-warning-bg;
      border-color: @color-tag-warning-border;
      color: @color-tag-warning-text;
    }

    &--success {
      background-color: @color-tag-success-bg;
      border-color: @color-success-light;
      color: @color-success;
    }
  }

  &__section {
    margin-top: 20px;

    &:first-of-type {
      margin-top: 8px;
    }
  }

  &__section-title {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: @color-text-light;
    margin: 0 0 10px 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    background-color: @color-bg-subtle;
    border-radius: 8px;
    padding: 16px 20px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--span-2 {
      grid-column: span 2;
    }

    &--span-3 {
      grid-column: span 3;
    }

    &--wrap .calculation-sheet__value {
      white-space: normal;
      overflow: visible;
    }
  }

  &__label {
    font-size: 13px;
    color: @color-text-light;
    margin-bottom: 2px;
  }

  &__value {
    font-size: 15px;
    color: @color-text-dark;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__suffix {
    color: @color-text-light;
    font-size: 13px;
  }

  &__hint {
    color: @color-text-muted;
    margin-left: 4px;
  }

  &__empty {
    font-size: 14px;
    color: @color-text-medium;
    background-color: @color-bg-subtle;
    border-radius: 8px;
    padding: 16px 20px;
    margin: 0;
  }

  &__total {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 12px 20px 0 20px;

    &--standalone {
      background-color: @color-bg-subtle;
      border-radius: 8px;
      padding: 16px 20px;
    }
  }

  &__total-label {
    font-size: 14px;
    color: @color-text-medium;
  }

  &__total-value {
    font-size: 20px;
    font-weight: 700;
    color: @color-text-dark;
  }
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.calculation-sheet-modal {
  .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: @color-text-dark;
  }

  .ant-modal-body {
    padding: 20px 24px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .ant-btn-primary {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }

  .calculation-sheet__banner--success a {
    color: @color-link;
    text-decoration: underline;

    &:hover {
      color: @color-link-hover;
    }
  }
}
</style>
